import React, { useCallback } from 'react';
import cn from 'classnames';
import { VdsIcon, IconSize } from '@el8/vital';
import { useGetUserSettingsSettingsGet } from '@elation/api-client/src/chart-assist-api/fastAPI';

import { useCurrentPractice } from 'modules/practice';
import { selectActiveChartPanelTab } from 'ui-modules/elation-ai/elationAISelectors';
import { setActiveChartPanelTab } from 'ui-modules/elation-ai/elationAISlice';
import useAppDispatch from 'utils/hooks/useAppDispatch';
import useAppSelector from 'utils/hooks/useAppSelector';
import toast from 'utils/notify/toast';
import { logElationAINavButtonClicked } from 'views/chart/chart-panel-tabs/amplitudeTaxonomy';
import { ChartPanelTabId } from 'views/chart/chart-panel-tabs/chartPanelTabsHelpers';

import { NavbarItem } from '../../components/NavbarItem';

import styles from './ElationAINavItem.less';

const SETTINGS_TIMEOUT_MS = 3000;
const SETTINGS_RETRY_COUNT = 2;
const SETTINGS_RETRY_DELAY_MS = 1000;
const MAX_SETTINGS_RETRY_DELAY_MS = 5000;

const getSettingsRetryDelay = (attemptIndex: number): number =>
  Math.min(SETTINGS_RETRY_DELAY_MS * 2 ** attemptIndex, MAX_SETTINGS_RETRY_DELAY_MS);

const handleSettingsError = (error: unknown): void => {
  console.error('Elation AI settings fetch failed:', error);
  toast('Elation AI is temporarily unavailable.');
};

/**
 * Navbar pill that activates the Elation AI chart panel tab and scrolls to it.
 * Only rendered on the patient chart page (gated in Navbar.tsx).
 */
export const ElationAINavItem = (): JSX.Element | null => {
  // Only check initial availability - disable all background refetches
  const { data: userSettings, isLoading: isSettingsLoading } = useGetUserSettingsSettingsGet({
    query: {
      onError: handleSettingsError,
      retry: SETTINGS_RETRY_COUNT,
      retryDelay: getSettingsRetryDelay,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
    request: { timeout: SETTINGS_TIMEOUT_MS },
  });
  const dispatch = useAppDispatch();
  const practiceId = useCurrentPractice().id;
  const activeTab = useAppSelector(selectActiveChartPanelTab);
  const isActive = activeTab === ChartPanelTabId.ElationAi;

  const handleClick = useCallback((): void => {
    logElationAINavButtonClicked(practiceId);
    dispatch(setActiveChartPanelTab(ChartPanelTabId.ElationAi));
    document.getElementById('react-chart-panel-tabs')?.scrollIntoView({ behavior: 'smooth' });
  }, [dispatch, practiceId]);

  if (!userSettings && !isSettingsLoading) {
    return null;
  }

  const intelligentIcon = (
    <VdsIcon
      icon="ai-filled"
      size={IconSize.XXSmall}
      appearance={isSettingsLoading ? 'default' : 'intelligent'}
    />
  );

  return (
    <NavbarItem
      aria-pressed={isActive}
      className={cn(
        styles.aiPill,
        isActive && styles.aiPillToggled,
        isSettingsLoading && styles.aiPillDisabled,
      )}
      data-testid="navbar-elation-ai"
      disabled={isSettingsLoading}
      icon={intelligentIcon}
      onClick={handleClick}
      title={isSettingsLoading ? 'Loading Elation AI user settings' : 'Launch Elation AI'}
      type="button"
    >
      {isSettingsLoading ? 'Loading...' : 'Elation AI'}
    </NavbarItem>
  );
};
